import { Alert, Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { getTrainingChecklist, TrainingChecklistItem } from "src/apis/upsell";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { formatDateToBR } from "src/utils/date";
import { Id } from "src/utils/types";

interface TrainingChecklistPreviewProps {
  companyId?: Id | '';
}

const TrainingChecklistPreview: FC<TrainingChecklistPreviewProps> = ({ companyId }) => {
  const [trainings, setTrainings] = useState<TrainingChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (!companyId) {
      setTrainings([]);
      return;
    }

    const fetchChecklist = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await getTrainingChecklist(companyId);
        setTrainings(response.trainings);
      } catch (err: any) {
        setError(err?.message || "Não foi possível carregar o checklist de treinamentos.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchChecklist();
  }, [companyId]);

  if (!companyId) {
    return (
      <Alert severity="warning">
        Não foi possível identificar sua empresa. Entre em contato com o suporte para continuar.
      </Alert>
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box>
        <Typography variant="h6">Checklist anual de treinamentos</Typography>
        <Typography variant="body2" color="text.secondary">
          Visualize o andamento das capacitações recomendadas para manter o engajamento e a conformidade.
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {isLoading ? (
        <SkeletonLoad variant="table" rows={5} columns={3} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Treinamento</strong></TableCell>
                <TableCell width={140}><strong>Status</strong></TableCell>
                <TableCell width={160}><strong>Última realização</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trainings.map((training) => (
                <TableRow key={`${training.slug ?? training.id ?? training.name}`}>
                  <TableCell>{training.name}</TableCell>
                  <TableCell>{training.status}</TableCell>
                  <TableCell>
                    {training.last_performed_at ? formatDateToBR(training.last_performed_at) : "-"}
                  </TableCell>
                </TableRow>
              ))}
              {trainings.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    Nenhum treinamento cadastrado para sua empresa.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default TrainingChecklistPreview;
